import type { CSSProperties } from "react";

export type SectionKey = "sopralluoghi" | "interventi" | "installazioni" | "attivazioni";

interface SectionColorDef {
  /** Colore base — usato per badge/icone piatte, non i tasti con rilievo. */
  base: string;
  /** Tono più chiaro, cima dello sfumato del tasto. */
  light: string;
  /** Tono più scuro, bordo inferiore "premibile" + ombra del tasto. */
  dark: string;
  /** Colore testo/icona leggibile sopra a questo colore. */
  textOn: string;
}

// Grigio (#87908b) e verde (#009f3c) sono i valori ESATTI trovati dentro
// winkoncash.svg — non varianti o approssimazioni. Blu e arancione non hanno
// un equivalente reale nei file caricati: l'arancione in particolare è stato
// scelto apposta diverso dall'oro del brand (#f3b149, già usato altrove
// nell'app) — riusarlo identico avrebbe reso Installazioni indistinguibile
// dal resto della UI invece che una sezione riconoscibile a colpo d'occhio.
export const SECTION_COLORS: Record<SectionKey, SectionColorDef> = {
  sopralluoghi: { base: "#87908b", light: "#9a9f9a", dark: "#6b716c", textOn: "#F1EFE8" },
  interventi: { base: "#2563eb", light: "#4a80f0", dark: "#1a4dc4", textOn: "#E6F1FB" },
  installazioni: { base: "#d96a12", light: "#f0812c", dark: "#a8500d", textOn: "#FAECE7" },
  attivazioni: { base: "#009f3c", light: "#17b34a", dark: "#06732b", textOn: "#EAF3DE" },
};

/**
 * Stile "a rilievo" per un tasto/tessera colorata di sezione: leggero
 * sfumato verticale + bordo inferiore più scuro (effetto "premibile") +
 * ombra morbida sotto. Va passato via prop `style`, non `className` — è una
 * combinazione troppo specifica per essere espressa comodamente in Tailwind.
 */
export function sectionTileStyle(key: SectionKey): CSSProperties {
  const c = SECTION_COLORS[key];
  return {
    background: `linear-gradient(180deg, ${c.light} 0%, ${c.base} 100%)`,
    boxShadow: `0 4px 0 ${c.dark}, 0 6px 10px rgba(0,0,0,0.18)`,
    color: c.textOn,
  };
}

/** Stile piatto (senza rilievo) per badge/icone di intestazione — solo il colore base. */
export function sectionFlatStyle(key: SectionKey): CSSProperties {
  const c = SECTION_COLORS[key];
  return { backgroundColor: c.base, color: c.textOn };
}

/** #rrggbb -> rgba(r,g,b,alpha). Serve per tinte leggere calcolate dal colore
 *  di sezione invece che scritte a mano: una tabella di tinte separata si
 *  sarebbe scollata dai colori base alla prima modifica. */
function withAlpha(hex: string, alpha: number): string {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

/**
 * Chip di filtro colorato per sezione (Calendario). Acceso: pieno nel colore
 * della sezione. Spento: fondo appena tinto e contorno nello stesso colore —
 * resta riconoscibile a colpo d'occhio anche da spento, cosa che il vecchio
 * grigio uguale per tutti non permetteva.
 *
 * Da spento il testo usa `dark`, non `base`: su un fondo così chiaro il grigio
 * dei Sopralluoghi (#87908b) non arriverebbe al contrasto minimo.
 */
export function sectionChipStyle(key: SectionKey, attivo: boolean): CSSProperties {
  const c = SECTION_COLORS[key];
  return attivo
    ? {
        backgroundColor: c.base,
        color: c.textOn,
        border: `1.5px solid ${c.base}`,
        boxShadow: `0 2px 5px ${withAlpha(c.base, 0.35)}`,
      }
    : {
        backgroundColor: withAlpha(c.base, 0.12),
        color: c.dark,
        border: `1.5px solid ${c.base}`,
      };
}
